import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import {
  CONTEXT_KEYS,
  TEMPLATE_TYPE,
  useArtboardContext,
} from '../../../context/complexArtboardContext';
import { ButtonBlockSidebar } from './ButtonBlockSidebar';
import TextBlockWithQuillRenderer from '../../ComplexDocumentDesign/TextBlockWithQuillRenderer';
import {
  constructAutoSaveJSON,
  constructQuillJSON,
  convertContractToReact,
} from '../../../utils/TextBlockHelper';
import { BUTTON_BLOCK } from '../../../utils/complexDocumentConstants';
import { useDefaultStyleContext } from '../../../context/complexDefaultStyleContext';
import BlockHighlight from '../../ComplexDocumentDesign/BlockHighlight';

export const dummyCallback = () => {};

const getButtonStyles = linkStyles => {
  const styles = {
    backgroundColor: linkStyles.buttonBg,
    padding: linkStyles.innerPadding,
    width: 'fit-content',
    display: 'inline-block',
  };
  if (linkStyles.boxShadow) {
    styles['boxShadow'] = linkStyles.boxShadow;
  }
  if (linkStyles.borderWidth) {
    styles['borderWidth'] = linkStyles.borderWidth + 'px';
    styles['borderStyle'] = linkStyles.borderStyle;
    styles['borderColor'] = linkStyles.borderColor;
  }
  if (linkStyles.borderRadius) {
    styles['borderRadius'] = linkStyles.borderRadius;
  }
  if (linkStyles.bgGradient) {
    styles['background'] = linkStyles.bgGradient;
  }
  return styles;
};

const ButtonBlockContainer = styled.div``;

export const ButtonBlock = props => {
  const { itemUUID, getElementStyles, parentUUID, editable, onClickBlockSelection, pageId } = props;
  const [textEditable, setTextEditable] = useState(false);
  const [enableSidebar, setEnableSidebar] = useState(false);
  const containerRef = useRef();
  const textEditorRef = useRef();

  const customSelector = useCallback(store => store[itemUUID], [itemUUID]);
  const [buttonBlock, store] = useDocumentContext(customSelector, `ButtonBlock-${itemUUID}`);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize: store.fontSize}), []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const documentStyleRef = useRef(documentStyle);

  const artboardContextSelector = useCallback(
    store => ({
      currentBlock: store[CONTEXT_KEYS.CURRENT_BLOCK] === itemUUID,
      selectedBlocks: store[CONTEXT_KEYS.SELECTED_BLOCKS],
      isDocumentOrFlowDesign: [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(
        store[CONTEXT_KEYS.CURRENT_DESIGN],
      ),
      isSettingsVisible: store[CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE],
    }),
    [],
  );
  const [
    { currentBlock, selectedBlocks, isDocumentOrFlowDesign, isSettingsVisible },
    updateArtboard,
  ] = useArtboardContext(artboardContextSelector, `ButtonBlock-${itemUUID}`);

  const variablesSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables] = useDocumentContext(variablesSelector, `ButtonBlock-${itemUUID}`);

  const handleDoubleClick = () => {
    if (editable && !currentBlock) {
      updateArtboard({
        [CONTEXT_KEYS.CURRENT_BLOCK]: itemUUID,
        [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: BUTTON_BLOCK,
        [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: true,
        [CONTEXT_KEYS.CURRENT_PAGE]: parentUUID,
        [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
        [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false,
      });
      setEnableSidebar(false);
    }
  };

  const handleSingleclick = e => {
    if (editable && isDocumentOrFlowDesign && !currentBlock) {
      if (e.ctrlKey || e.metaKey) {
        onClickBlockSelection({ itemUUID, parentUUID });
      }
    }
  };

  const closeSidebar = () => {
    updateArtboard({
      [CONTEXT_KEYS.CURRENT_BLOCK]: '',
      [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false,
      [CONTEXT_KEYS.CURRENT_PAGE]: '',
      [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: '',
    });
  };

  const updateContent = useCallback((
    elContents,
    undoDiff,
    redoDiff,
    cb,
    ref,
    op = undefined,
    newGlobalVariables = [],
  ) => {
    if (fontChangeRef.current) {
      fontChangeRef.current = false;
      cb();
      return;
    }
    const newContents = constructAutoSaveJSON(elContents, documentStyle?.fontFamily, documentStyle?.fontSize, newGlobalVariables);
    store.updateBlock('text-contents', itemUUID, {
      contents: newContents,
      flowUUID: parentUUID,
      type: 'block',
      historyChange: {
        undo: undoDiff,
        redo: redoDiff,
        op: op,
      },
    });
  }, [parentUUID, documentStyle]);

  const convertToQuill = useCallback(
    editable => {
      if (editable) {
        // save scroll state of the current page and complex-page-wrapper element and apply
        // the scroll state once quill renders the content to avoid jumping of the cursor
        // and viewport when invoking the quill to render the content.
        const parentLayout = document.getElementById(`vertical-layout-${pageId}`);
        const complexPageWrapperEl = document.getElementById('complex-page-wrapper');
        const scrollElTopOld = complexPageWrapperEl.scrollTop;
        const currentScrollTop = parentLayout.scrollTop;
        setTextEditable(editable);
        return () => {
          parentLayout.scrollTop = currentScrollTop;
          complexPageWrapperEl.scrollTop = scrollElTopOld;
        };
      }
      return;
    },
    [pageId],
  );

  const { contents, style } = buttonBlock;
  const { padding, backgroundColor, alignment, linkStyles } = style;
  const blockSelected = selectedBlocks.includes(itemUUID);
  const fontChangeRef = useRef(null);

  useEffect(() => {
    const handleFontStyleUpdate = e => {
      if (!editable) return;
      const { action, newFontFamilyOrSize, styleType } = e.detail;
      const eventData =
        action === 'update'
          ? {
              fontStyleData: newFontFamilyOrSize,
              styleType,
              action,
            }
          : { action };
      if (textEditorRef?.current) {
        fontChangeRef.current = true;
        documentStyleRef.current = Object.assign({}, documentStyleRef.current, {
          [styleType]: newFontFamilyOrSize,
        });
        setTextEditable(true);
        setTimeout(() => {
          textEditorRef.current.dispatchEvent(
            new CustomEvent('updateFontFamilySizeEvent', { detail: eventData }),
          );
        }, 500);
      }
    };
    documentStyleRef.current = documentStyle;
    document.addEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    return () => {
      document.removeEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    };
  }, []);

  useEffect(() => {
    const updateLinkStyles = () => {
      if (textEditorRef?.current) {
        fontChangeRef.current = true;
        setTimeout(() => {
          textEditorRef.current.dispatchEvent(
            new CustomEvent('updateFontFamilySizeEvent', {
              detail: { action: 'update' },
            }),
          );
        }, 500);
      }
    };
    const cleanup = containerRef.current;
    if (cleanup) {
      cleanup.addEventListener('updateLinkStyles', updateLinkStyles);
    }
    return () => cleanup?.removeEventListener('updateLinkStyles', updateLinkStyles);
  }, []);

  return (
    <>
      <ButtonBlockContainer
        id={`button-block-${itemUUID}`}
        ref={containerRef}
        data-uuid={itemUUID}
        style={{
          position: 'relative',
          backgroundColor: blockSelected ? '#ACCEF7' : backgroundColor,
          textAlign: alignment,
          padding:
            padding &&
            `${padding?.top}px ${padding?.right}px ${padding?.bottom}px ${padding?.left}px`,
          zIndex: currentBlock && 2,
        }}
        onMouseMove={e =>
          editable && !currentBlock && getElementStyles && getElementStyles(e, itemUUID)
        }
        onDoubleClick={handleDoubleClick}
        onClick={handleSingleclick}
      >
        <div style={{ ...getButtonStyles(linkStyles), position: 'relative', zIndex: 1 }}>
          <TextBlockWithQuillRenderer
            defaultContent={constructQuillJSON(
              contents,
              documentStyleRef.current?.fontFamily || documentStyle?.fontFamily,
              documentStyleRef.current?.fontSize || documentStyle?.fontSize,
              globalVariables,
            )}
            saveBlock={updateContent}
            itemUUID={itemUUID}
            selected={currentBlock}
            enableSideBar={setEnableSidebar}
            enableAI={false}
            toolbarInvisible={true}
            adjustScroll={dummyCallback}
            textEditable={textEditable}
            setTextEditable={convertToQuill}
            ref={textEditorRef}
            enableFunctionCall={true}
          >
            {!textEditable
              ? convertContractToReact(
                  contents,
                  documentStyle?.fontFamily,
                  documentStyle?.fontSize,
                  globalVariables,
                )
              : null}
          </TextBlockWithQuillRenderer>
        </div>
        {currentBlock && backgroundColor === undefined && <BlockHighlight pageUUID={parentUUID} />}
      </ButtonBlockContainer>
      {currentBlock && isSettingsVisible && !enableSidebar && (
        <ButtonBlockSidebar
          buttonBlock={buttonBlock}
          globalVariables={globalVariables}
          parentUUID={parentUUID}
          store={store}
          closeSidebar={closeSidebar}
        />
      )}
    </>
  );
};
